import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const solutions = [
  { icon: 'ri-flashlight-line', title: 'Instant Response', detail: 'Via SMS, voice, or chat — under 60 seconds, every time.', color: '#FF2B2B' },
  { icon: 'ri-brain-line', title: 'Intelligent Qualification', detail: 'AI filters and ranks leads so your team only handles what matters.', color: '#3FA9F5' },
  { icon: 'ri-calendar-check-line', title: 'Automated Booking', detail: 'Appointments get scheduled without any human intervention.', color: '#FF2B2B' },
  { icon: 'ri-refresh-line', title: 'Follow-Up Sequences', detail: 'Consistent, personalized messages keep every lead warm.', color: '#3FA9F5' },
  { icon: 'ri-database-2-line', title: 'CRM Integration', detail: 'Seamlessly syncs with your existing tools and workflows.', color: '#FF2B2B' },
];

export default function SolutionSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#080808]' : 'bg-white'}`}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      {/* Blue glow */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full bg-[#3FA9F5]/5 blur-3xl -translate-y-1/2 pointer-events-none"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div
          className="mb-20"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <span className="text-[#3FA9F5] text-xs font-bold tracking-widest uppercase block mb-6">The Solution</span>
          <h2
            className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 max-w-3xl ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
            style={{ letterSpacing: '-0.03em' }}
          >
            A Smarter System<br />
            <span className="text-[#3FA9F5]">That Works Instantly.</span>
          </h2>
          <p className={`text-lg md:text-xl max-w-xl leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
            We build AI-powered systems that engage every lead the moment they reach out.<br />
            <strong className={isDark ? 'text-white' : 'text-[#1D1D1F]'}>No delays. No missed opportunities.</strong>
          </p>
        </div>

        {/* Feature grid — Apple-style 2-col layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Left: large feature card */}
          <div
            className={`rounded-3xl overflow-hidden ${isDark ? 'bg-[#0D0D0D] border border-[#1A1A1A]' : 'bg-[#F5F5F7] border border-[#E8E8ED]'}`}
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateX(-30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s' }}
          >
            <div className="relative w-full" style={{ height: 280 }}>
              <img
                src="https://public.readdy.ai/ai/img_res/6e16a393fe63e9678c0020d3cfbb8877.jpg"
                alt="AI system"
                className="w-full h-full object-cover object-top"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0D0D0D]' : 'from-[#F5F5F7]'} via-transparent to-transparent`}></div>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse inline-block"></span>
                <span className={`text-xs font-semibold tracking-widest uppercase ${isDark ? 'text-[#C9CED6]/50' : 'text-[#6E6E73]'}`}>Always Active</span>
              </div>
              <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>Your AI system never sleeps.</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`}>
                Every lead that reaches out — day or night — receives an instant, intelligent response.
                Your business is always open.
              </p>
            </div>
          </div>

          {/* Right: solution list */}
          <div className="flex flex-col gap-4">
            {solutions.map((s, i) => (
              <div
                key={s.title}
                className={`flex items-center gap-5 p-5 rounded-2xl border transition-all duration-300 ${isDark ? 'bg-[#0D0D0D] border-[#1A1A1A] hover:border-[#3FA9F5]/20' : 'bg-[#F5F5F7] border-[#E8E8ED] hover:border-[#3FA9F5]/30'}`}
                style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateX(30px)', transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${0.15 + i * 0.08}s` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${s.color}15` }}>
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
                </div>
                <div>
                  <div className={`font-semibold text-sm mb-0.5 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{s.title}</div>
                  <div className={`text-xs leading-relaxed ${isDark ? 'text-[#C9CED6]/55' : 'text-[#6E6E73]'}`}>{s.detail}</div>
                </div>
                <div className="ml-auto w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-[#3FA9F5] text-sm"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
